/* eslint-disable react/display-name */
import { Button, Form, Input, Select, Space, Tag, Typography } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styles from 'src/styles/layout.module.css';
import { useForm } from 'antd/es/form/Form';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'src/index';
import LoaderWrapper from 'src/components/atoms/LoaderWrapper';
import tileStyles from 'src/styles/tile.module.css';
import { formatServiceType } from 'src/utils/utils';
import { RoleEnum } from 'src/features/users/users.types';
import ComponentRoleProtected from 'src/components/blocks/ComponentRoleProtected';
import {
  createCompanyServiceProvider,
  deleteCompanyServiceProvider,
  fetchCompanyServiceProviders,
} from '../../../../../features/serviceProvider/state';
import { useServiceProviderById } from '../../../../../features/serviceProvider/hooks';
import { NewServiceProvider, ServiceProviderType } from '../../../../../features/serviceProvider/types';
import ConfirmButton from '../../../../atoms/ConfirmButton';

interface CreateServiceProps {
  onCreate: (req: NewServiceProvider) => void;
}

const CreateServiceForm: React.FC<CreateServiceProps> = props => {
  const { onCreate } = props;
  const [form] = useForm();

  const onFinish = (values: NewServiceProvider) => onCreate(values);

  return (
    <Form
      form={form}
      layout={'vertical'}
      onFinish={onFinish}
      initialValues={{ provider: ServiceProviderType.HotelService }}
    >
      <Form.Item name='provider' label={'Сервис'} required>
        <Select defaultActiveFirstOption={true} placeholder='Выбирете сервис' autoFocus>
          <Select.Option value={ServiceProviderType.HotelService} key={ServiceProviderType.HotelService}>
            {formatServiceType(ServiceProviderType.HotelService)}
          </Select.Option>
        </Select>
      </Form.Item>
      <Form.Item name='authKey' label={'Ключ'} required>
        <Input type='text' name='authKey' placeholder='Введите ключ' />
      </Form.Item>
      <Form.Item shouldUpdate>
        {() => (
          <Button
            type='primary'
            htmlType='submit'
            disabled={
              !form.isFieldsTouched(['provider', 'authKey']) ||
              !!form.getFieldsError().filter(({ errors }) => errors.length).length
            }
          >
            Submit
          </Button>
        )}
      </Form.Item>
    </Form>
  );
};

const CompanyServicesTab: React.FC = () => {
  const { companyId } = useParams() as { companyId: string };
  const [createMode, setCreateMode] = useState<boolean>(false);
  const dispatch: AppDispatch = useDispatch();
  const providers = useServiceProviderById(companyId);

  useEffect(() => {
    dispatch(fetchCompanyServiceProviders({ companyId }));
  }, [dispatch, companyId]);

  const createHandler = useCallback(
    async (data: NewServiceProvider) => {
      const response = await dispatch(
        createCompanyServiceProvider({
          companyId,
          data,
        })
      );
      if (createCompanyServiceProvider.fulfilled.match(response)) {
        setCreateMode(false);
      }
    },
    [dispatch, companyId]
  );

  const onDelete = async (providerId: string) => {
    await dispatch(
      deleteCompanyServiceProvider({
        companyId,
        providerId,
      })
    );
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Typography.Title level={4}>Сервисы</Typography.Title>
        <ComponentRoleProtected roles={[RoleEnum.admin, RoleEnum.observer, RoleEnum.companyContentManager]}>
          {createMode ? (
            <Button onClick={() => setCreateMode(false)} icon={<CloseOutlined />} />
          ) : (
            <Button onClick={() => setCreateMode(true)} icon={<PlusOutlined />} />
          )}
        </ComponentRoleProtected>
      </div>
      <div className={styles.content}>
        {createMode ? (
          <div>
            <Typography.Title level={4}>Добавить сервис</Typography.Title>
            <CreateServiceForm onCreate={createHandler} />
          </div>
        ) : (
          <LoaderWrapper loading={false}>
            {providers.map(b => (
              <div
                key={b.id}
                className={tileStyles.listTile}
                style={{ height: '85px', width: '100%', display: 'flex' }}
              >
                <Space direction={'vertical'} size={1} style={{ width: '100%', marginRight: 30 }}>
                  <Typography.Text style={{ fontSize: 18, margin: 0 }}>{formatServiceType(b.provider)}</Typography.Text>
                  <Input.Password value={b.authKey} style={{ width: '100%' }} />
                </Space>
                <Space align='center'>
                  <Button
                    danger
                    type='dashed'
                    onClick={() => {
                      onDelete(b.id);
                    }}
                  >
                    Удалить
                  </Button>
                </Space>
              </div>
            ))}
          </LoaderWrapper>
        )}
      </div>
    </div>
  );
};

export default CompanyServicesTab;
